import { isChargingEnabled } from './assets/types/chargingController/chargingControllerState';
import { connectAssetChildrenWithParents } from './lib/asset/children';
import { storeManagerEndpoints } from '@/lib/endpoints';
import endpoint from '@/util/endpoint';
import { Asset, AssetDefect, CreateAssetDefectTicketRequest } from '@whiz-cart/node-shared/models/erp/asset';
import { AssetDefectTicketStatus } from '@whiz-cart/node-shared/models/erp/assetDefectTicket';
import { AssetLocation, AssetLocationType } from '@whiz-cart/node-shared/models/erp/assetLocation';
import { AssetMetrics } from '@whiz-cart/node-shared/models/erp/assetMetrics';
import { UiAsset } from '@whiz-cart/node-shared/models/erp/uiAsset';
import { service } from '@whiz-cart/node-shared/service/service';
import { createCache } from 'cross-state';
import _ from 'lodash';

/** How long a charger toggle waits for the controller state to reflect the change. */
export const CHARGER_TOGGLE_TIMEOUT_MS = 3 * 60 * 1000;

/** The controller state did not reflect a charger toggle within the timeout. */
export class ChargerToggleTimeoutError extends Error {
    // matched by name in known-error lists, kept as a property so minification cannot rename it
    static readonly errorName = 'ChargerToggleTimeoutError';
    override readonly name = ChargerToggleTimeoutError.errorName;
    /** mm:ss, the format the store manager backend uses in its own timeout message */
    readonly timeout: string;

    constructor(timeoutMs: number) {
        const seconds = Math.round(timeoutMs / 1000);
        const timeout = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
        super(`Charger did not reach the requested state within ${timeout}`);
        this.timeout = timeout;
    }
}

export const assetService = service(
    'assetService',
    class AssetService {
        getStoreAssets = createCache(
            async (storeGuid: string) => {
                const response = await endpoint('storeManager.getStoreAssets', { storeGuid }).get();
                const parsedAssets = UiAsset.array().parse(response);
                connectAssetChildrenWithParents(parsedAssets);
                return parsedAssets;
            },
            { invalidateAfter: { minutes: 1 } },
        );

        getStoreAsset(storeGuid: string, assetId: string) {
            return this.getStoreAssets(storeGuid).mapValue((assets) => {
                return assets.find((asset) => asset.id === assetId) ?? null;
            });
        }

        getLocationAssets = createCache(
            async (locationId: string) => {
                const response = await endpoint('storeManager.getLocationAssets', { locationId }).get();
                const parsedAssets = UiAsset.array().parse(response);
                connectAssetChildrenWithParents(parsedAssets);
                return parsedAssets;
            },
            { invalidateAfter: { minutes: 1 }, clearOnInvalidate: false },
        );

        getLocationAsset(locationId: string, assetId: string) {
            return this.getLocationAssets(locationId).mapValue((assets) => assets.find((asset) => asset.id === assetId) ?? null);
        }

        getAssetById = createCache(async (assetId: string) => {
            const response = await endpoint('storeManager.getAssetById', { assetId }).get();
            return UiAsset.parse(response);
        });

        getAsset(locationType: 'store' | 'assetLocation', locationId: string, assetId: string) {
            if (locationType === 'store') {
                return this.getStoreAsset(locationId, assetId);
            }

            return this.getLocationAsset(locationId, assetId);
        }

        getAssetMetrics = createCache(
            async ({
                storeGuid,
                assetId,
                metric,
                start = 'now() - 24h',
                end = 'now()',
                binning = '5m',
                aggregate = 'mean',
            }: {
                storeGuid: string;
                assetId: string;
                metric: string;
                start?: string;
                end?: string;
                binning?: string;
                aggregate?: string;
            }) => {
                const metrics = await endpoint('storeManager.getAssetMetrics', { storeGuid, assetId, metric }).get({
                    params: { start, end, binning, aggregate },
                });

                return AssetMetrics.parse(metrics);
            },
            {
                invalidateAfter: { minutes: 1 },
            },
        );

        async toggleCharger(storeGuid: string, assetId: string, state: boolean) {
            await endpoint('storeManager.toggleChargingController', { assetId }).post(state);
            // confirmed by the working power supplies only, a broken one never reports on
            await this.whenStateUpdated(storeGuid, assetId, (asset) => isChargingEnabled(asset) === state);
        }

        /**
         * Resolves once `predicate` holds for the asset in the store asset list, which is refreshed every 5 s.
         * Rejects with ChargerToggleTimeoutError after `timeoutMs`: the controller state travels
         * controller -> store asset manager -> cloud and normally shows up within two minutes.
         */
        async whenStateUpdated(
            storeGuid: string,
            assetId: string,
            predicate: (asset: UiAsset) => boolean,
            timeoutMs = CHARGER_TOGGLE_TIMEOUT_MS,
        ) {
            const interval = setInterval(() => this.getStoreAssets(storeGuid).get({ update: 'force' }), 5_000);
            const abort = new AbortController();
            const timeout = setTimeout(() => abort.abort(), timeoutMs);

            try {
                await this.getStoreAssets(storeGuid).state.once(
                    ({ value: assets }) => {
                        const asset = assets?.find((asset) => asset.id === assetId);
                        return !!asset && predicate(asset);
                    },
                    { signal: abort.signal },
                );
            } catch (error) {
                throw abort.signal.aborted ? new ChargerToggleTimeoutError(timeoutMs) : error;
            } finally {
                clearInterval(interval);
                clearTimeout(timeout);
            }
        }

        async createAsset(asset: Asset): Promise<Asset> {
            const response = await endpoint('storeManager.createAsset').put(asset);
            this.getStoreAssets.invalidateAll();
            this.getLocationAssets.invalidateAll();
            const result = Asset.parse(response);
            this.getAssetById(result.id).updateValue(result);

            return result;
        }

        async updateAsset(asset: Asset): Promise<Asset> {
            const response = await endpoint('storeManager.updateAsset').patch(asset);
            this.getStoreAssets.invalidateAll();
            this.getLocationAssets.invalidateAll();
            const result = Asset.parse(response);
            this.getAssetById(result.id).updateValue(result);

            return result;
        }

        async deleteAsset(asset: Asset | string): Promise<void> {
            await endpoint('storeManager.deleteAsset', { assetId: typeof asset === 'string' ? asset : asset.id }).delete(asset);
            this.getStoreAssets.invalidateAll();
            this.getLocationAssets.invalidateAll();
        }

        getAssetBySerialNumber = createCache(async (serialNumber: string) => {
            const response = await endpoint('storeManager.getAssetBySerialNumber', { serialNumber }).get();
            return Asset.nullish().parse(response);
        });

        getAssetLocations = createCache(async () => {
            const response = await endpoint('storeManager.getAssetLocations').get();
            return AssetLocation.array().parse(response);
        });

        listAssetLocations = this.getAssetLocations.mapValue((locations) =>
            Object.fromEntries(locations.map((location) => [location.id, location])),
        );

        async updateAssetLocation(assetLocation: AssetLocation) {
            await endpoint('storeManager.updateAssetLocation').post(assetLocation);
            this.getAssetLocations.invalidateAll();
        }

        async deleteAssetLocation(assetLocation: AssetLocation) {
            await endpoint('storeManager.deleteAssetLocation', { id: assetLocation.id }).delete();
            this.getAssetLocations.invalidateAll();
        }

        getAssetLocationTypes = createCache(async () => {
            const response = await endpoint('storeManager.getAssetLocationTypes').get();
            return AssetLocationType.array().parse(response);
        });

        listAssetLocationTypes = this.getAssetLocationTypes.mapValue((types) => Object.fromEntries(types.map((type) => [type.id, type])));

        async updateAssetLocationType(assetLocationType: AssetLocationType) {
            await endpoint('storeManager.updateAssetLocationType').post(assetLocationType);
            this.getAssetLocationTypes.invalidateAll();
        }

        async deleteAssetLocationType(resourceLocationType: AssetLocationType) {
            await endpoint('storeManager.deleteAssetLocationType', { id: resourceLocationType.id }).delete();
            this.getAssetLocationTypes.invalidateAll();
        }

        listDefects = createCache(
            async (storeGuid: string, assetGuid: string) => {
                const response = await storeManagerEndpoints.assets.tickets.listAssetDefects().fetch({
                    query: {
                        storeGuid,
                        assetGuid,
                        limit: 100,
                    },
                });
                return _.orderBy(response.items, 'createdOn', 'desc');
            },
            {
                invalidateAfter: {
                    minutes: 1,
                },
            },
        );

        async createAssetDefect(defect: CreateAssetDefectTicketRequest) {
            const response = await storeManagerEndpoints.assets.tickets.createAssetDefect().fetch(defect);

            this.listDefects(defect.storeGuid, defect.assetGuid).invalidate();
            this.getStoreAssets.invalidateAll();
            this.getLocationAssets.invalidateAll();

            return response;
        }

        async cancelAssetDefect(assetDefectTicketGuid: string) {
            const response = await storeManagerEndpoints.assets.tickets.cancelAssetDefect().fetch({ assetDefectTicketGuid });

            this.listDefects.invalidateAll();
            this.getStoreAssets.invalidateAll();
            this.getLocationAssets.invalidateAll();

            return response;
        }

        getAssetDefectTicketSettings = createCache(
            (storeGuid: string) => {
                return storeManagerEndpoints.assets.tickets.getTicketSettings({ storeGuid }).fetch();
            },
            {
                clearOnInvalidate: false,
                invalidateAfter: {
                    hours: 1,
                },
            },
        );
    },
);

export function isAssetDefectOpen(defect: AssetDefect) {
    return (
        defect.externalAssetDefectTicket?.status !== AssetDefectTicketStatus.Resolved &&
        defect.externalAssetDefectTicket?.status !== AssetDefectTicketStatus.Closed
    );
}
